/**
 * Pure billing-preview calculation helpers for Advance Outlet Invoicing.
 * Kept local to AOI so Order List / other BillingWithOffer modules are unaffected.
 */

const sumBy = (rows, key) =>
	rows.reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0);

const getBaseTaxableAmt = (detailsObj) => {
	const newRate =
		(detailsObj.rate * 100) /
		(100 + detailsObj.sgstPer + detailsObj.cgstPer + detailsObj.igstPer);
	return newRate * detailsObj.qty;
};

const recalculateDetailFromItemDiscPer = (
	detailsObj,
	itemDiscPer,
	discountOnBillPer,
) => {
	const newTaxableAmt = getBaseTaxableAmt(detailsObj);
	const newItemWiseDiscount = (newTaxableAmt / 100) * itemDiscPer;
	const newTaxableWithItemWiseDiscount = newTaxableAmt - newItemWiseDiscount;
	const newBillWiseDiscount =
		(newTaxableWithItemWiseDiscount / 100) * discountOnBillPer;
	const newTaxableWithBillWiseDiscount =
		newTaxableWithItemWiseDiscount - newBillWiseDiscount;
	const newSgstAmt =
		(newTaxableWithItemWiseDiscount / 100) * detailsObj.sgstPer;
	const newCgstAmt =
		(newTaxableWithItemWiseDiscount / 100) * detailsObj.cgstPer;
	const newIgstAmt =
		(newTaxableWithItemWiseDiscount / 100) * detailsObj.igstPer;

	return {
		...detailsObj,
		cgstAmt: newSgstAmt,
		sgstAmt: newCgstAmt,
		igstAmt: newIgstAmt,
		itemDiscAmt: (newTaxableWithBillWiseDiscount / 100) * itemDiscPer,
		taxableAmt: newTaxableWithBillWiseDiscount,
		totalAmt:
			newTaxableWithBillWiseDiscount + newSgstAmt + newCgstAmt + newIgstAmt,
		itemDiscPer,
	};
};

const withBillTotals = (currentBill, updatedDetails, extraFields = {}) => ({
	...currentBill,
	...extraFields,
	taxableAmt: sumBy(updatedDetails, "taxableAmt"),
	totalAmt: sumBy(updatedDetails, "totalAmt"),
	cgstAmt: sumBy(updatedDetails, "cgstAmt"),
	sgstAmt: sumBy(updatedDetails, "sgstAmt"),
	igstAmt: sumBy(updatedDetails, "igstAmt"),
	itemDistcountAmt: updatedDetails.reduce(
		(sum, row) => sum + (parseFloat(row.itemDiscAmt) || 0),
		0,
	),
	outletBillDetails: updatedDetails,
});

/** Recalculate a bill when item discount % changes */
export const applyItemDiscPerChange = (
	outletBillsAfterApplyingOffers,
	detailId,
	fieldName,
	value,
) => {
	if (
		!outletBillsAfterApplyingOffers ||
		Object.keys(outletBillsAfterApplyingOffers).length === 0 ||
		!outletBillsAfterApplyingOffers.outletBillDetails?.length
	) {
		return null;
	}

	if (value > 100) {
		return null;
	}

	const updatedDetails = outletBillsAfterApplyingOffers.outletBillDetails.map(
		(detailsObj) => {
			if (detailId !== detailsObj.id) {
				return detailsObj;
			}
			return {
				...recalculateDetailFromItemDiscPer(
					detailsObj,
					value,
					outletBillsAfterApplyingOffers.discountOnBillPer,
				),
				[fieldName]: value,
			};
		},
	);

	return withBillTotals(outletBillsAfterApplyingOffers, updatedDetails);
};

/** Recalculate a bill when item discount amount changes */
export const applyItemDiscAmtChange = (
	outletBillsAfterApplyingOffers,
	detailId,
	value,
) => {
	if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
		return null;
	}

	if (
		!outletBillsAfterApplyingOffers ||
		Object.keys(outletBillsAfterApplyingOffers).length === 0 ||
		!outletBillsAfterApplyingOffers.outletBillDetails?.length
	) {
		return null;
	}

	const updatedDetails = outletBillsAfterApplyingOffers.outletBillDetails.map(
		(detailsObj) => {
			if (detailId !== detailsObj.id) {
				return detailsObj;
			}

			const grossAmt = detailsObj.rate * detailsObj.qty;
			const enteredDiscAmt =
				value === "" || isNaN(parseFloat(value)) ? 0 : parseFloat(value);
			const clampedDiscAmt = Math.min(enteredDiscAmt, grossAmt);
			const amtAfterDiscount = grossAmt - clampedDiscAmt;
			const totalTaxPer =
				detailsObj.sgstPer + detailsObj.cgstPer + detailsObj.igstPer;
			const newTaxableAmt = amtAfterDiscount / (1 + totalTaxPer / 100);
			const newSgstAmt = (newTaxableAmt / 100) * detailsObj.sgstPer;
			const newCgstAmt = (newTaxableAmt / 100) * detailsObj.cgstPer;
			const newIgstAmt = (newTaxableAmt / 100) * detailsObj.igstPer;
			const newTotalAmt =
				newTaxableAmt + newSgstAmt + newCgstAmt + newIgstAmt;
			const derivedDiscPer =
				grossAmt > 0 ? (clampedDiscAmt / grossAmt) * 100 : 0;

			return {
				...detailsObj,
				itemDiscAmt: value,
				itemDiscPer: parseFloat(derivedDiscPer.toFixed(4)),
				taxableAmt: newTaxableAmt,
				cgstAmt: newCgstAmt,
				sgstAmt: newSgstAmt,
				igstAmt: newIgstAmt,
				totalAmt: newTotalAmt,
			};
		},
	);

	return withBillTotals(outletBillsAfterApplyingOffers, updatedDetails);
};

/** Recalculate a bill when CD / discountOnBillPer changes */
export const applyBillCdChange = (
	outletBillsAfterApplyingOffers,
	billId,
	fieldName,
	value,
) => {
	if (
		!outletBillsAfterApplyingOffers ||
		Object.keys(outletBillsAfterApplyingOffers).length === 0
	) {
		return null;
	}

	if (billId !== outletBillsAfterApplyingOffers.id || value > 100) {
		return null;
	}

	const updatedDetails = outletBillsAfterApplyingOffers.outletBillDetails.map(
		(detailsObj) =>
			recalculateDetailFromItemDiscPer(
				detailsObj,
				detailsObj.itemDiscPer,
				value,
			),
	);

	const taxableAmt = sumBy(updatedDetails, "taxableAmt");

	return withBillTotals(outletBillsAfterApplyingOffers, updatedDetails, {
		[fieldName]: value,
		discountOnBillAmt: (taxableAmt / 100) * value,
	});
};

export const buildBillDataToSave = (
	billObject,
	vehicleName,
	parseDiscountFields,
) => {
	const outletBill = billObject.outletBillsAfterApplyingOffers;

	if (parseDiscountFields) {
		return {
			...outletBill,
			discountOnBillPer: parseFloat(outletBill.discountOnBillPer),
			vehicleNo: vehicleName,
			outletBillDetails:
				outletBill.outletBillDetails?.map((billDetailsObject) => ({
					...billDetailsObject,
					itemDiscPer: parseFloat(billDetailsObject.itemDiscPer),
					itemDiscAmt: parseFloat(billDetailsObject.itemDiscAmt) || 0,
				})) || [],
		};
	}

	return {
		...outletBill,
		vehicleNo: vehicleName,
	};
};

export const deductFreeQtyFromStock = (stockList, offerBillList) => {
	const stockDummyNewList = stockList;

	for (let i = 0; i < stockDummyNewList.length; i++) {
		offerBillList.forEach((billObject) => {
			const details =
				billObject.outletBillsAfterApplyingOffers?.outletBillDetails || [];
			for (let j = 0; j < details.length; j++) {
				if (stockDummyNewList[i].productId == details[j].product.id) {
					stockDummyNewList[i].stock =
						stockDummyNewList[i].stock - details[j].freeQty;
				}
			}
		});
	}

	return stockDummyNewList;
};
